import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, TextInput } from 'design-system';
import getTypeDisplayName from '@scale/shared/get-type-display-name';
import { Section } from 'react-scale/containers/corp/backfills/batch/components/Section';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JSONEditable from '../LidarliteLintingPage/Components/JSONEditable';
import { Button } from 'design-system/Button';
import useToggle from 'react-use-v13/lib/useToggle';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  MlLinterSection,
  enabledTaskType,
  MLLinterConfigWrapper,
  LinterDocsType,
  UPDATE_LINTER_CONFIG_DOCS_URL,
} from '@scale/shared/types/corp/linterConfigDocs';
import { LinterArguments, LintExamples, styles } from './LinterConfigDocsComponents';
import { map, set, includes, without } from 'lodash';

const LinterMLConfig = ({
  linter,
  linterDocsJson,
  selected = true,
  isPreview = false,
  onLinterUpdate,
  getStyle,
}: {
  linter: MlLinterSection;
  linterDocsJson: LinterDocsType;
  selected: boolean;
  isPreview?: boolean;
  onLinterUpdate: () => void;
  getStyle: (selected: boolean) => Record<string, string>;
}) => {
  const classes = styles();
  const [isEdit, toggleIsEdit] = useToggle(false);
  const linterRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (selected) {
      linterRef?.current?.scrollIntoView();
    }
  }, []);
  return (
    <div
      id={`${linter.scriptName}`}
      style={{ maxWidth: 1000, width: '90%', ...getStyle(selected) }}
      ref={linterRef}
    >
      <Section
        name={
          <div style={{ display: 'inline-block' }}>
            <span>{linter.name}</span>
            <div style={{ display: 'inline', padding: '4px 12px', marginLeft: '6px' }}></div>
            <FontAwesomeIcon
              icon="edit"
              size="xs"
              onClick={event => {
                if (!isPreview) {
                  event.stopPropagation();
                  toggleIsEdit();
                }
              }}
            />
          </div>
        }
        forceHidden={!(selected || isEdit)}
        linterSelected={selected}
        sectionId={linter.scriptName}
      >
        {!isEdit ? (
          <div>
            <div className={classes.linterSectionBody}>
              <SanitizedMarkdown text={`**Script Name:** ${linter.scriptName}`} />
              <SanitizedMarkdown text={`**Description:** ${linter.description}`} />
              <div>
                <b>Allowed Task Types:</b>{' '}
                {linter.allowedTaskTypes.map((type, i) => (
                  <div key={i} className={classes.pill}>
                    <span>&nbsp;{type}&nbsp;</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, marginBottom: 40 }}>
                <b>Model:</b>{' '}
                <a href={linter.model.url} target={'_blank'} rel={'noopener noreferrer'}>
                  {linter.model.name}
                </a>
              </div>
              <h3>Configuration</h3>
              {Object.entries(linter.config).map(([name, object]) => (
                <LinterArguments
                  key={name}
                  name={name}
                  type={object.type}
                  defaultValue={object.default}
                  description={object.description}
                />
              ))}
            </div>
            <div className={classes.linterSectionBody}>
              <div>
                <h3>Example Linters</h3>
                {linter.examples.length > 0 && <LintExamples examples={linter.examples} />}
              </div>
            </div>
          </div>
        ) : (
          <AddMLConfigLinter
            linterDocsJson={linterDocsJson}
            currentLinter={linter}
            onLinterUpdate={onLinterUpdate}
            isEdit={true}
          />
        )}
      </Section>
    </div>
  );
};

export const AddMLConfigLinter = (props: {
  linterDocsJson: LinterDocsType;
  currentLinter: MlLinterSection;
  isEdit?: boolean;
  onLinterUpdate: () => void;
  popupClose?: () => void;
}) => {
  const { linterDocsJson, currentLinter, onLinterUpdate, popupClose, isEdit = false } = props;
  const [newLinterConfig, setNewLinterConfig] = useState<MlLinterSection>({
    ...currentLinter,
  });
  const currentLinterName = `${currentLinter.name}`;
  const newSetValue = (path: string, newValue: any): void => {
    let configCopy = { ...newLinterConfig };
    configCopy = set(configCopy, path, newValue);
    setNewLinterConfig(configCopy);
  };
  const handleTaskTypeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    includes(newLinterConfig.allowedTaskTypes, e.target.value)
      ? newSetValue('allowedTaskTypes', without(newLinterConfig.allowedTaskTypes, e.target.value))
      : newSetValue('allowedTaskTypes', [...newLinterConfig.allowedTaskTypes, e.target.value]);
  };
  const editConfigName = (argName: string, newValue: string): void => {
    if (argName !== newValue && newLinterConfig.config[argName]) {
      const keys = Object.keys(newLinterConfig.config);
      const configCopy = keys.reduce((acc: MLLinterConfigWrapper, oldName) => {
        if (oldName === argName) {
          acc[newValue] = newLinterConfig.config[oldName];
        } else {
          acc[oldName] = newLinterConfig.config[oldName];
        }
        return acc;
      }, {});
      newSetValue('config', configCopy);
    }
  };
  const addNewConfigArg = () => {
    const configCopy = { ...newLinterConfig.config };
    configCopy['Config Name'] = {
      type: '',
      description: '',
      default: '',
    };
    newSetValue('config', configCopy);
  };
  const deleteConfigArg = (argName: string): void => {
    const configCopy = { ...newLinterConfig.config };
    delete configCopy[argName];
    newSetValue('config', configCopy);
  };
  const addNewConfigExample = () => {
    const examplesCopy = [...newLinterConfig.examples];
    examplesCopy.push({ name: '', configJson: {} });
    newSetValue('examples', examplesCopy);
  };
  const deleteConfigExample = (index: number): void => {
    const examplesCopy = [...newLinterConfig.examples];
    examplesCopy.splice(index, 1);
    newSetValue('examples', examplesCopy);
  };
  const classes = styles();
  const saveChanges = async () => {
    const updatedJson = { ...linterDocsJson };
    const existingLinterIndex = linterDocsJson.mlLinters.findIndex(
      linterObj => linterObj.name === newLinterConfig.name,
    );
    if (existingLinterIndex >= 0) {
      updatedJson.mlLinters[existingLinterIndex] = newLinterConfig;
    } else if (isEdit) {
      const oldLinterIndex = linterDocsJson.mlLinters.findIndex(
        linterObj => linterObj.name === currentLinterName,
      );
      updatedJson.mlLinters[oldLinterIndex] = newLinterConfig;
    } else {
      updatedJson.mlLinters.push(newLinterConfig);
    }
    try {
      await apiFetch(null, UPDATE_LINTER_CONFIG_DOCS_URL, {
        method: 'POST',
        body: updatedJson,
      });
      if (popupClose) {
        popupClose();
      }
      onLinterUpdate();
    } catch (error) {
      defaultApiFetchErrorHandler(error as any, { handleHttp: true });
    }
  };
  return (
    <div>
      <div className={classes.tripleColumnWrapper}>
        <div>
          <TextInput
            className={classes.tripleColumn}
            label="Linter Name"
            value={newLinterConfig.name}
            onChange={e => newSetValue('name', e.target.value)}
            size="large"
            fullWidth
          />
        </div>
        <div>
          <TextInput
            className={classes.tripleColumn}
            label="Script Name"
            value={newLinterConfig.scriptName}
            onChange={e => newSetValue('scriptName', e.target.value)}
            size="large"
            fullWidth
          />
        </div>
      </div>
      <div>
        <h3>Allowed Task Types:</h3>
        <form className={classes.taskTypeCheckbox}>
          {enabledTaskType.map((taskType, i) => (
            <div key={`allowedTask${i}`}>
              <Checkbox
                label={getTypeDisplayName(taskType) || taskType}
                value={taskType}
                checked={includes(newLinterConfig.allowedTaskTypes, taskType)}
                onChange={handleTaskTypeCheckbox}
              />
            </div>
          ))}
        </form>
      </div>
      <div>
        <TextInput
          label="Description"
          value={newLinterConfig.description}
          onChange={e => newSetValue('description', e.target.value)}
          multiline={true}
          size="large"
          fullWidth
          rows={3}
        />
      </div>
      <div style={{ display: 'flex', marginTop: 40, marginBottom: 40 }}>
        <TextInput
          className={classes.tripleColumn}
          label="Model Name"
          value={newLinterConfig.model.name}
          onChange={e => newSetValue('model.name', e.target.value)}
          size="large"
          style={{ width: 460 }}
        />
        <TextInput
          className={classes.tripleColumn}
          label="Model URL"
          value={newLinterConfig.model.url}
          onChange={e => newSetValue('model.url', e.target.value)}
          size="large"
          fullWidth
        />
      </div>
      <div>
        <h3>Configuration Fields:</h3>
        <form>
          {map(newLinterConfig.config, (argObject, argName, collection) => (
            <div key={Object.keys(collection).indexOf(argName)} style={{ padding: '10px 0px' }}>
              <div className={classes.tripleColumnWrapper} style={{ height: 60 }}>
                <div>
                  <TextInput
                    className={classes.tripleColumn}
                    label="Field Name"
                    value={argName}
                    onChange={e => editConfigName(argName, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
                <div>
                  <TextInput
                    className={classes.tripleColumn}
                    label="Field Type (`string`, `number`, `array`)"
                    value={argObject.type}
                    onChange={e => newSetValue(`config.${argName}.type`, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
                <div>
                  <TextInput
                    className={classes.tripleColumn}
                    label="Default Value (Optional)"
                    value={argObject.default ?? ''}
                    onChange={e => newSetValue(`config.${argName}.default`, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
              </div>
              <div className={classes.doubleColumnWrapper}>
                <div className={classes.widerColumn}>
                  <TextInput
                    label="Description"
                    value={argObject.description}
                    onChange={e => newSetValue(`config.${argName}.description`, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
                <div className={classes.thinnerColumn}>
                  <Button type="error" onClick={() => deleteConfigArg(argName)}>
                    <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </form>
      </div>
      <div>
        <Button type="primary" onClick={addNewConfigArg} style={{ margin: 5 }}>
          <FontAwesomeIcon icon="plus" style={{ marginRight: 5 }} /> Add a Linter Config Field
        </Button>
      </div>
      <div>
        <h3>Examples</h3>
        <form>
          {newLinterConfig.examples.map((exampleObject, index) => (
            <div key={`example${index}`}>
              <div className={classes.doubleColumnWrapper}>
                <div className={classes.widerColumn}>
                  <TextInput
                    label="Example Name"
                    value={exampleObject.name}
                    onChange={e => newSetValue(`examples[${index}].name`, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
                <div className={classes.thinnerColumn}>
                  <Button type="error" onClick={() => deleteConfigExample(index)}>
                    <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} /> Remove
                  </Button>
                </div>
              </div>
              <div className={classes.tripleColumn}>
                <h4>Example JSON</h4>
                <JSONEditable
                  onEditJSON={e => newSetValue(`examples[${index}].configJson`, e)}
                  isShowingRaw={true}
                  json={exampleObject.configJson}
                />
              </div>
            </div>
          ))}
        </form>
      </div>
      <div className={classes.tripleColumn}>
        <Button type="primary" onClick={addNewConfigExample}>
          <FontAwesomeIcon icon="plus" style={{ marginRight: 5 }} /> Add an Example
        </Button>
      </div>
      <div>
        <h3>Preview</h3>
        <LinterMLConfig
          linter={newLinterConfig}
          linterDocsJson={linterDocsJson}
          onLinterUpdate={onLinterUpdate}
          isPreview={true}
          selected={false}
          getStyle={(selected: boolean) =>
            selected ? { background: '#e8e8e8' } : { background: 'transparent' }
          }
        />
      </div>
      <Button type="primary" onClick={saveChanges}>
        <FontAwesomeIcon icon="save" style={{ marginRight: 5 }} /> Save
      </Button>
    </div>
  );
};
